import { markRaw } from 'vue';
import { IPassage } from '../../models/Passage';
import { ActivePigRun, PipelineSectionMarker } from '../../models/ActivePigRun';
import {
  PigRunStoreState,
  PigRunMapEntry,
  PipelineSectionMarkerState,
  PigLocationState,
  UpdatePigLocationPayload,
  UpdateMarkerPayload,
  LaunchPigRunPayload,
} from '../../models/PigRunState';
import PigRunService from '../../services/PigRunService';

/** In-flight dedup: if a fetch is already in progress, return the same promise. */
let activePigRunsInflight: Promise<void> | null = null;

const getPigRunPendingIds: Set<number> = new Set();
const getPigRunLastFetchTime: Map<number, number> = new Map();
const GET_PIG_RUN_THROTTLE_MS = 10000;

/**
 * Builds a PigRunMapEntry from an ActivePigRun API response.
 * Preserves the existing pigLocation if an entry already exists in the map.
 */
function buildMapEntry(
  pigRun: ActivePigRun,
  existingEntry?: PigRunMapEntry
): PigRunMapEntry {
  const markers: PipelineSectionMarkerState[] = [...pigRun.markers]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((m: PipelineSectionMarker) => ({
      pipelineSectionMarkerId: m.pipelineSectionMarkerId,
      name: m.name,
      trackState: m.trackState,
      description: m.description,
      pigRunId: m.pigRunId,
      autoVerify: m.autoVerify,
      latitude: m.latitude,
      longitude: m.longitude,
      sortOrder: m.sortOrder,
      manualLinkDevices: m.manualLinkDevices,
    }));

  return {
    pigRun: markRaw(pigRun),
    markers,
    pigLocation: existingEntry?.pigLocation ?? null,
  };
}

export default {
  state: {
    pigRuns: {} as Record<number, PigRunMapEntry>,
    visiblePigRunIds: [] as number[],
    missedPassages: [] as IPassage[],
    selectedPigRunId: null as number | null,
    isReportLoading: false,
    isRCompRunSynching: false,
    showMLPanel: false,
  } as PigRunStoreState,
  mutations: {
    // ── Active pig run map mutations ────────────────────────────

    /** Bulk-load all active pig runs into the map (initial fetch / full refresh). */
    SET_PIG_RUNS(state: PigRunStoreState, payload: ActivePigRun[]) {
      const newMap: Record<number, PigRunMapEntry> = {};
      for (const pigRun of payload) {
        newMap[pigRun.pigRunId] = buildMapEntry(
          pigRun,
          state.pigRuns[pigRun.pigRunId]
        );
      }
      state.pigRuns = newMap;
      state.visiblePigRunIds = payload.map((pr) => pr.pigRunId);
    },

    /** Upsert a single pig run (after single-run API refresh). */
    SET_PIG_RUN(state: PigRunStoreState, pigRun: ActivePigRun) {
      state.pigRuns = {
        ...state.pigRuns,
        [pigRun.pigRunId]: buildMapEntry(
          pigRun,
          state.pigRuns[pigRun.pigRunId]
        ),
      };
      if (!state.visiblePigRunIds.includes(pigRun.pigRunId)) {
        state.visiblePigRunIds = [...state.visiblePigRunIds, pigRun.pigRunId];
      }
    },

    /** Remove a cancelled or completed pig run from the active map and the list. */
    REMOVE_ACTIVE_PIG_RUN(state: PigRunStoreState, pigRunId: number) {
      const { [pigRunId]: _, ...rest } = state.pigRuns;
      state.pigRuns = rest;
      state.visiblePigRunIds = state.visiblePigRunIds.filter(
        (id) => id !== pigRunId
      );
      if (state.selectedPigRunId === pigRunId) {
        state.selectedPigRunId = null;
      }
    },

    /** Update pig lat/lon/speed/ETA for a single run (high-frequency SignalR). */
    UPDATE_PIG_LOCATION(
      state: PigRunStoreState,
      payload: UpdatePigLocationPayload
    ) {
      const entry = state.pigRuns[payload.pigRunId];
      if (!entry) return;

      entry.pigLocation = {
        lat: payload.lat,
        lon: payload.lon,
        speed: payload.speed,
        serverTime: payload.serverTime,
        etaToNext: payload.etaToNext,
        etaToNextTrack: payload.etaToNextTrack,
        etaToEnd: payload.etaToEnd,
      };
    },

    /** Partial update of a marker's fields (state change, description change, etc.). */
    UPDATE_MARKER(state: PigRunStoreState, payload: UpdateMarkerPayload) {
      const entry = state.pigRuns[payload.pigRunId];
      if (!entry) return;

      const idx = entry.markers.findIndex(
        (m) => m.pipelineSectionMarkerId === payload.pipelineSectionMarkerId
      );
      if (idx !== -1) {
        entry.markers[idx] = { ...entry.markers[idx], ...payload.fields };
        entry.markers = [...entry.markers];
        state.pigRuns = { ...state.pigRuns };
      }
    },

    /** Control which pig runs are rendered on the map. */
    SET_VISIBLE_PIG_RUN_IDS(state: PigRunStoreState, ids: number[]) {
      state.visiblePigRunIds = ids;
    },

    /** Insert a newly launched live-track pig run from the launch API response. */
    LAUNCH_PIG_RUN(state: PigRunStoreState, payload: LaunchPigRunPayload) {
      const { response, toolName, toolType, launchDate } = payload;

      const markers: PipelineSectionMarkerState[] = [...response.markers]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((m) => ({
          pipelineSectionMarkerId: m.pipelineSectionMarkerId,
          name: m.name,
          trackState: m.trackState,
          description: m.description,
          pigRunId: response.pigRunId,
          autoVerify: false,
          latitude: m.latitude,
          longitude: m.longitude,
          sortOrder: m.sortOrder,
          manualLinkDevices: [],
        }));

      const pigRun: ActivePigRun = {
        pigRunId: response.pigRunId,
        pipelineSectionConfigurationId: 0,
        type: 'LT',
        lifecycleState: 'A',
        name: toolName,
        launchDate,
        jobNumber: '',
        expectedSpeed: 0,
        isArchived: false,
        markers: response.markers as any,
        devices: [],
        tools: [],
      };

      const entry: PigRunMapEntry = {
        pigRun,
        markers,
        pigLocation: null,
      };

      state.pigRuns = { ...state.pigRuns, [response.pigRunId]: entry };
      if (!state.visiblePigRunIds.includes(response.pigRunId)) {
        state.visiblePigRunIds = [...state.visiblePigRunIds, response.pigRunId];
      }
    },

    SET_MISSED_PASSAGES(state: PigRunStoreState, payload: IPassage[]) {
      state.missedPassages = payload;
    },
    CLEAR_MISSED_PASSAGES(state: PigRunStoreState) {
      state.missedPassages = [];
    },
    REMOVE_MISSED_PASSAGE(state: PigRunStoreState, payload: IPassage) {
      state.missedPassages = state.missedPassages.filter(
        (p: IPassage) => p.passageId !== payload.passageId
      );
    },
    SET_SELECTED_PIG_RUN_ID(state: PigRunStoreState, pigRunId: number | null) {
      // if pigRunId is null, find the next active pig run from the active map
      if (pigRunId === null) {
        const activeIds = Object.keys(state.pigRuns).map(Number);
        state.selectedPigRunId = activeIds.length > 0 ? activeIds[0] : null;
        return;
      }

      state.selectedPigRunId = pigRunId;
    },
    CLEAR_SELECTED_PIG_RUN_ID(state: PigRunStoreState) {
      state.selectedPigRunId = null;
    },
    SET_REPORT_LOADING_STATUS(
      state: PigRunStoreState,
      isReportLoading: boolean
    ) {
      state.isReportLoading = isReportLoading;
    },
    SET_REPORT_SYNCH_STATUS(
      state: PigRunStoreState,
      isRCompRunSynching: boolean
    ) {
      state.isRCompRunSynching = isRCompRunSynching;
    },
    SET_SHOW_ML_PANEL(state: PigRunStoreState, show: boolean) {
      state.showMLPanel = show;
    },
  },
  getters: {
    /** Returns active pig runs as a flat array for backward compatibility. */
    activePigRunsList(state: PigRunStoreState): ActivePigRun[] {
      return Object.values(state.pigRuns).map((entry) => entry.pigRun);
    },
  },
  actions: {
    async getActivePigRuns(context: {
      commit: (type: string, payload?: unknown) => void;
    }) {
      if (activePigRunsInflight) return activePigRunsInflight;
      activePigRunsInflight = (async () => {
        try {
          const response = await PigRunService.getActivePigRuns();
          const activePigRuns: ActivePigRun[] = response.data;
          context.commit('SET_PIG_RUNS', activePigRuns);
        } catch (error) {
          console.error('Failed to fetch active pig runs:', error);
          context.commit('SET_PIG_RUNS', []);
        } finally {
          activePigRunsInflight = null;
        }
      })();
      return activePigRunsInflight;
    },
    async refreshSinglePigRun(
      context: { commit: (type: string, payload?: unknown) => void },
      pigRunId: number
    ) {
      try {
        if (getPigRunPendingIds.has(pigRunId)) return;
        const lastFetch = getPigRunLastFetchTime.get(pigRunId) ?? 0;
        if (Date.now() - lastFetch < GET_PIG_RUN_THROTTLE_MS) return;
        getPigRunPendingIds.add(pigRunId);
        getPigRunLastFetchTime.set(pigRunId, Date.now());
        const response = await PigRunService.getActivePigRun(pigRunId);
        context.commit('SET_PIG_RUN', response.data);
      } catch (error) {
        console.error(`Failed to refresh pig run ${pigRunId}:`, error);
      } finally {
        getPigRunPendingIds.delete(pigRunId);
      }
    },
    showPigRunManager(
      context: { commit: (type: string, payload?: unknown) => void },
      payload: unknown
    ) {
      context.commit('SHOW_PIG_RUN_MANAGER', payload);
    },
    hidePigRunManager(
      context: { commit: (type: string, payload?: unknown) => void },
      payload: unknown
    ) {
      context.commit('HIDE_PIG_RUN_MANAGER', payload);
    },
    showReportManager(
      context: { commit: (type: string, payload?: unknown) => void },
      payload: unknown
    ) {
      context.commit('SHOW_REPORT_MANAGER', payload);
    },
    showBroadcastCommands(
      context: { commit: (type: string, payload?: unknown) => void },
      payload: unknown
    ) {
      context.commit('SHOW_BROADCAST_COMMANDS', payload);
    },
    removeMissedPassage(
      context: { commit: (type: string, payload?: unknown) => void },
      passage: IPassage
    ) {
      context.commit('REMOVE_MISSED_PASSAGE', passage);
    },
    triggerManualAlert(
      context: { dispatch: (type: string, payload?: unknown) => void },
      payload: { markerName: string; runId: number }
    ) {
      PigRunService.triggerManualAlert(payload.markerName, payload.runId)
        .then(() => {
          // only expecting 200 response
        })
        .catch((error: unknown) => {
          console.error(error);
          context.dispatch('notify', error);
        });
    },

    async SynchFinishedPigRun(
      context: {
        commit: (type: string, payload?: unknown) => void;
        dispatch: (type: string, payload?: unknown) => void;
      },
      pigRunId: number
    ) {
      // Status tracking only — list refresh is handled locally in PigRunManager
      context.commit('SET_REPORT_SYNCH_STATUS', true);
      try {
        await PigRunService.synchFinishedPigRun(pigRunId);
      } catch (error) {
        console.error('Failed to sync pig run:', error);
      } finally {
        context.commit('SET_REPORT_SYNCH_STATUS', false);
      }
    },

    // Event bus actions — no state/mutations, used to subscribe to actions in other components
    addManualMarkerPassage(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    addMarkerNote(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    changeMarkerDescription(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    changeMarkerState(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    assignMarkerUid(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    unlinkMarkerUid(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    calculateMarkerEta(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    refreshMarkerEta(_context: unknown, _markerEtas: number) {},
    verifyMarkerPassage(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    refreshPigRun(_context: unknown, _runId: number) {},
    refreshcompPigRun(_context: unknown, _runId: number) {},
    pigRunsReady(_context: unknown, _runId: number) {},
    updatePigLoc(_context: unknown, _info: unknown) {},
    updatePigLocV2(_context: unknown, _info: unknown) {},
    passRunId(_context: unknown, _runId: number) {},

    pigProgressMaximizeRequest(_context: unknown, _runId: number) {},
    pigProgressMinimizeRequest(_context: unknown, _runId: number) {},
  },
};
